import { TransportRequestError } from "@/transport"
import { getUserFacingErrorMessage } from "@coval/ui"
import { DisconnectReason } from "livekit-client"

export function getSimulationRoomErrorMessage(error: unknown): string {
  if (error instanceof TransportRequestError && error.status === 409) {
    if (error.detail?.code === "simulation_not_started") {
      return "This simulation has not started, so live audio is not available yet."
    }
    if (error.detail?.code === "simulation_not_live") {
      return "This simulation has finished, so live audio is no longer available."
    }
    if (error.detail?.code === "simulation_room_unavailable") {
      return "Live audio is not available in this environment right now."
    }
    return "Live audio is not available for this simulation right now."
  }

  return getUserFacingErrorMessage(error, {
    fallback: "Could not start live audio.",
  })
}

/**
 * A friendly message for an unrecoverable LiveKit disconnect — one this
 * client did not ask for (deliberate stop() is already filtered out before
 * this runs). CLIENT_INITIATED is the one reason that would otherwise be
 * unrecoverable-looking but isn't; every other reason means the connection
 * dropped or was ended out from under the listener.
 */
const DISCONNECT_REASON_MESSAGES: Partial<Record<DisconnectReason, string>> = {
  [DisconnectReason.DUPLICATE_IDENTITY]:
    "This listener was disconnected because another tab or window joined the same room.",
  [DisconnectReason.SERVER_SHUTDOWN]:
    "The live room was closed. Live audio is no longer available for this simulation.",
  [DisconnectReason.ROOM_DELETED]:
    "The live room was closed. Live audio is no longer available for this simulation.",
  [DisconnectReason.ROOM_CLOSED]:
    "The live room was closed. Live audio is no longer available for this simulation.",
  [DisconnectReason.STATE_MISMATCH]:
    "Lost the connection to live audio. Try listening again.",
  [DisconnectReason.SIGNAL_CLOSE]:
    "Lost the connection to live audio. Try listening again.",
  [DisconnectReason.JOIN_FAILURE]:
    "Lost the connection to live audio. Try listening again.",
}

export function getDisconnectReasonMessage(
  reason: DisconnectReason | undefined,
) {
  if (reason === undefined) {
    return "The live audio connection was lost unexpectedly."
  }
  return (
    DISCONNECT_REASON_MESSAGES[reason] ??
    "The live audio connection was lost unexpectedly."
  )
}
